import type { AxiosResponse } from "axios";
import apiClient from "./apiBase";
import type {
  AutoTopupResponse,
  CreditActivity,
  CreditCheckoutPayload,
  CreditPricing,
  CreditState,
  InvoiceListResponse,
  RedirectResponse,
} from "./billing.types";
import type {
  ApiKeyCreatedResponse,
  ApiKeyListResponse,
  CargoPayload,
  CreateApiKeyPayload,
  DeleteAccountPayload,
  FollowShipmentPayload,
  IdentityPayload,
  PendingShipmentPayload,
  ProfileImageKind,
  ProfileResponse,
  QubicListResponse,
  Shipment,
  ShipmentDetail,
  ShipmentDocument,
  ShipmentDocumentListResponse,
  ShipmentListResponse,
  SubscriptionListResponse,
  UpdatePasswordPayload,
  UpdateProfilePayload,
  UploadDocumentOptions,
} from "./cabinet.types";
import type { ActivityListResponse, UsageSeriesResponse } from "./dashboard.types";
import type {
  AirSearchParams,
  AirTrackingResponse,
  CarrierListResponse,
  TrackingResponse,
  TrackingSearchParams,
} from "./tracking.types";

import { resolveBackofficeOrigin } from "@qubictron/url/client";

const API_KEYS_URL = "/api/api-keys";
const SUBSCRIPTIONS_URL = "/api/subscriptions";
const QUBICS_URL = "/api/qubics";
const PROFILE_URL = "/api/user/me";
const USAGE_DAILY_URL = "/api/usage/daily";
const ACTIVITY_URL = "/api/activity";
const SHIPMENTS_URL = "/api/shipments";
const CREDITS_URL = "/api/credits";
const BILLING_URL = "/api/billing";
const PASSWORD_URL = "/api/user/password";
const TRACKING_SEARCH_URL = "/api/tracking/search";
/**
 * Gateway paths reached through the back office proxy, which swaps the session
 * for the company's API key. Ocean search has its own route because guests use
 * it too; these two are for signed-in visitors only.
 */
const CARRIERS_URL = "/api/tools/tracking/v1/ocean/carriers";
const AIR_SEARCH_URL = "/api/tools/tracking/v1/air";

export class ApiKeysApi {
  async list(): Promise<AxiosResponse<ApiKeyListResponse>> {
    return apiClient.get(API_KEYS_URL);
  }

  async create(payload: CreateApiKeyPayload): Promise<AxiosResponse<ApiKeyCreatedResponse>> {
    return apiClient.post(API_KEYS_URL, payload);
  }

  async revoke(id: number): Promise<AxiosResponse<void>> {
    return apiClient.delete(`${API_KEYS_URL}/${id}`);
  }
}

export class ShipmentsApi {
  /**
   * POST, not GET: the list is filtered and ordered through a request body,
   * which is the shape the tracking widget already speaks.
   */
  async list(): Promise<AxiosResponse<ShipmentListResponse>> {
    return apiClient.post(`${SHIPMENTS_URL}/get`);
  }

  /** One shipment's detail page. Another company's shipment is a 404, not a 403. */
  async show(id: number): Promise<AxiosResponse<ShipmentDetail>> {
    return apiClient.get(`${SHIPMENTS_URL}/${id}`);
  }

  /**
   * Start tracking a shipment. The back office keys cards on company plus
   * reference plus type, so following one twice updates it instead of adding
   * a duplicate.
   */
  async follow(payload: FollowShipmentPayload): Promise<AxiosResponse<Shipment>> {
    return apiClient.post(SHIPMENTS_URL, payload);
  }

  /**
   * Save a shipment that has no number yet. Nothing is looked up: the card is
   * a placeholder for a booking still to be confirmed, so it costs no tracking
   * call and carries only the route it is meant to run.
   */
  async addPending(payload: PendingShipmentPayload): Promise<AxiosResponse<Shipment>> {
    return apiClient.post(`${SHIPMENTS_URL}/pending`, payload);
  }

  async remove(id: number): Promise<AxiosResponse<void>> {
    return apiClient.delete(`${SHIPMENTS_URL}/${id}`);
  }

  /** The detail page's identity fields. Answers with the card only, without the grid. */
  async update(id: number, payload: IdentityPayload): Promise<AxiosResponse<Shipment>> {
    return apiClient.put(`${SHIPMENTS_URL}/${id}`, payload);
  }

  /** Replaces the container grid. Answers with the whole detail page, as `show` does. */
  async updateCargo(id: number, payload: CargoPayload): Promise<AxiosResponse<ShipmentDetail>> {
    return apiClient.put(`${SHIPMENTS_URL}/${id}/cargo`, payload);
  }
}

/**
 * Files attached to a shipment. They are only ever read through the back
 * office, which checks the company on every request — there is no direct
 * link to the disk.
 */
export class ShipmentDocumentsApi {
  /** Newest first. `containerId` narrows it: "none" is shipment-level only. */
  async list(id: number, containerId?: number | "none"): Promise<AxiosResponse<ShipmentDocumentListResponse>> {
    return apiClient.get(`${SHIPMENTS_URL}/${id}/documents`, {
      params: containerId === undefined ? undefined : { container_id: containerId },
    });
  }

  /** Multipart, the way the profile images are sent. Answers with the new document. */
  async upload(id: number, file: File, options: UploadDocumentOptions = {}): Promise<AxiosResponse<ShipmentDocument>> {
    const body = new FormData();
    body.append("file", file);

    if (options.name !== undefined) body.append("name", options.name);
    if (options.container_id !== undefined) body.append("container_id", String(options.container_id));

    return apiClient.post(`${SHIPMENTS_URL}/${id}/documents`, body);
  }

  async remove(id: number, documentId: number): Promise<AxiosResponse<{ deleted: boolean }>> {
    return apiClient.delete(`${SHIPMENTS_URL}/${id}/documents/${documentId}`);
  }
}

/**
 * The document's bytes on the back office origin. Inline opens it in the
 * browser for a preview; otherwise the back office sends it as a download.
 * A plain link is enough: the session cookie travels with it.
 */
export const documentFileUrl = (document: ShipmentDocument, inline = false): string =>
  `${resolveBackofficeOrigin()}${document.url}${inline ? "?disposition=inline" : ""}`;

/**
 * The gateway, reached the way the tracking widget reaches it: through the
 * back office, which holds the API key and meters the call against the
 * company's plan.
 */
export class TrackingApi {
  async search(params: TrackingSearchParams): Promise<AxiosResponse<TrackingResponse>> {
    return apiClient.get(TRACKING_SEARCH_URL, { params });
  }

  async searchAir(params: AirSearchParams): Promise<AxiosResponse<AirTrackingResponse>> {
    return apiClient.get(AIR_SEARCH_URL, { params });
  }

  async carriers(): Promise<AxiosResponse<CarrierListResponse>> {
    return apiClient.get(CARRIERS_URL);
  }
}

export class SubscriptionsApi {
  async list(): Promise<AxiosResponse<SubscriptionListResponse>> {
    return apiClient.get(SUBSCRIPTIONS_URL);
  }
}

/**
 * The Qubics Store catalogue. Asked for whole: the kind and category tabs
 * filter it in place, so switching tabs never waits on the network.
 */
export class QubicsApi {
  async list(): Promise<AxiosResponse<QubicListResponse>> {
    return apiClient.get(QUBICS_URL);
  }
}

/**
 * The dashboard's own two reads. Both are company-scoped and both answer with
 * an empty shape rather than an error when there is nothing yet.
 */
export class UsageApi {
  async daily(days: number): Promise<AxiosResponse<UsageSeriesResponse>> {
    return apiClient.get(USAGE_DAILY_URL, { params: { days } });
  }
}

export class ActivityApi {
  async list(limit: number): Promise<AxiosResponse<ActivityListResponse>> {
    return apiClient.get(ACTIVITY_URL, { params: { limit } });
  }
}

/**
 * QCredits: the balance, its price list and its activity, buying and the
 * monthly auto top-up. Credits arrive by Stripe webhook, never from these
 * calls, so a purchase shows up on the next read after Checkout returns.
 */
export class CreditsApi {
  async show(): Promise<AxiosResponse<CreditState>> {
    return apiClient.get(CREDITS_URL);
  }

  /** Public: the same tiers and costs the back office charges with. */
  async pricing(): Promise<AxiosResponse<{ data: CreditPricing }>> {
    return apiClient.get(`${CREDITS_URL}/pricing`);
  }

  /** `month` is YYYY-MM; left out, the current month. */
  async activity(month?: string): Promise<AxiosResponse<CreditActivity>> {
    return apiClient.get(`${CREDITS_URL}/activity`, { params: month === undefined ? {} : { month } });
  }

  /** Answers with the Stripe Checkout address: a one-time pack, or the monthly auto top-up. */
  async checkout(payload: CreditCheckoutPayload): Promise<AxiosResponse<RedirectResponse>> {
    return apiClient.post(`${CREDITS_URL}/checkout`, payload);
  }

  /** The new amount applies from the next charge; nothing is charged today. */
  async changeAutoTopup(credits: number): Promise<AxiosResponse<AutoTopupResponse>> {
    return apiClient.post(`${CREDITS_URL}/auto-topup/change`, { credits });
  }

  async cancelAutoTopup(): Promise<AxiosResponse<AutoTopupResponse>> {
    return apiClient.post(`${CREDITS_URL}/auto-topup/cancel`);
  }

  async resumeAutoTopup(): Promise<AxiosResponse<AutoTopupResponse>> {
    return apiClient.post(`${CREDITS_URL}/auto-topup/resume`);
  }
}

/** Stripe's side of billing: invoices and the customer portal. */
export class BillingApi {
  async invoices(): Promise<AxiosResponse<InvoiceListResponse>> {
    return apiClient.get(`${BILLING_URL}/invoices`);
  }

  /** Answers with a one-off Stripe Customer Portal address. */
  async portal(): Promise<AxiosResponse<RedirectResponse>> {
    return apiClient.post(`${BILLING_URL}/portal`);
  }
}

export class ProfileApi {
  async show(): Promise<AxiosResponse<ProfileResponse>> {
    return apiClient.get(PROFILE_URL);
  }

  async update(payload: UpdateProfilePayload): Promise<AxiosResponse<ProfileResponse>> {
    return apiClient.patch(PROFILE_URL, payload);
  }

  async updatePassword(payload: UpdatePasswordPayload): Promise<AxiosResponse<void>> {
    return apiClient.put(PASSWORD_URL, payload);
  }

  async destroy(payload: DeleteAccountPayload): Promise<AxiosResponse<void>> {
    return apiClient.delete(PROFILE_URL, { data: payload });
  }

  /**
   * The avatar and the cover have routes of their own because the body is a
   * file: POST multipart rather than PATCH JSON. Both of these answer with the
   * whole profile, so the card repaints from the response without a second GET.
   */
  async uploadImage(kind: ProfileImageKind, file: File): Promise<AxiosResponse<ProfileResponse>> {
    const body = new FormData();
    body.append("image", file);

    return apiClient.post(`${PROFILE_URL}/${kind}`, body);
  }

  async removeImage(kind: ProfileImageKind): Promise<AxiosResponse<ProfileResponse>> {
    return apiClient.delete(`${PROFILE_URL}/${kind}`);
  }
}

export const apiKeysApi = new ApiKeysApi();
export const shipmentsApi = new ShipmentsApi();
export const trackingApi = new TrackingApi();
export const shipmentDocumentsApi = new ShipmentDocumentsApi();
export const subscriptionsApi = new SubscriptionsApi();
export const qubicsApi = new QubicsApi();
export const profileApi = new ProfileApi();
export const usageApi = new UsageApi();
export const activityApi = new ActivityApi();
export const creditsApi = new CreditsApi();
export const billingApi = new BillingApi();

export * from "./billing.types";
export * from "./cabinet.types";
export * from "./dashboard.types";
export * from "./tracking.types";
